import {
  ChangeEvent,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';

import {
  AngleDown,
  Button,
  ChakraBox,
  ChakraFlex,
  ChakraGridItem,
  ChakraSimpleGrid,
  ChakraSpacer,
  DateFormat,
  DatePicker,
  EquipUIFireflyV2Theme,
  Menu,
  MenuOption,
  PhoneNumber,
  TextInput,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import ConfirmModal from '~/components/common/ConfirmModal';
import useUnsavedChangesGuard from '~/lib/hooks/useUnsavedChangesGuard';
import PayorSelect from './PayorSelect';
import { ApiCommand } from '~/lib/Api';
import { MAX_NAME_LENGTH } from '~/lib/constants';
import { PatientCoverage } from '~/lib/constants/patients.constants';
import urlConstants from '~/lib/constants/url.constants';
import { useApi } from '~/lib/hooks';
import { toStateMenuOptionInCoverage } from '~/lib/util/menu';
import {
  getPatientCoveragePriorityOptions,
  getPatientCoveragePriorityTextByValue,
  isCurrentCoveragePriorityAlreadyExists,
} from '~/lib/util/patientCoverage.util';
import util from '~/lib/util/util';
import usePatientCoveragesDispatch from '~/store/hooks/patientCoverages';
import {
  useGetCoverageSubscriberRelationsToPatientQuery,
  useGetProviderStatesEnumQuery,
} from '~/store/services/enumerations';

interface CoverageMenuProps<T> {
  buttonContent: ReactNode;
  id?: string;
  isInvalid?: boolean;
  isLoading?: boolean;
  isRequired?: boolean;
  label: string;
  options: MenuOption[];
  placeholder: string;
  setState: Dispatch<SetStateAction<T>>;
  withPlaceholderItem?: boolean;
}
interface PatientAddCoverageProps {
  coverage?: PatientCoverage;
  onAdd: any;
  onDiscard: any;
  patientId: string;
  patientBasicDetails: Patient;
  patientCoverages: PatientCoverage[];
}

const { primary } = EquipUIFireflyV2Theme.colors;

const { putCoverage, postCoverage } = urlConstants.clinical_data;

type AddCoverageErrors = {
  status?: boolean;
  payor?: boolean;
  groupId?: boolean;
  stateCode?: boolean;
  subscriberId?: boolean;
};

type AddCoverageErrorsAction = {
  field: keyof AddCoverageErrors | 'SET';
  error?: boolean;
  newState?: AddCoverageErrors;
};

type AddCoverageErrorsReducer = (
  state: AddCoverageErrors,
  action: AddCoverageErrorsAction,
) => AddCoverageErrors;

const errorsReducer: AddCoverageErrorsReducer = (
  state,
  action,
): AddCoverageErrors => {
  const { field, error: fieldError = false, newState = {} } = action;
  let updatedState: AddCoverageErrors = newState;

  if (field !== 'SET') {
    updatedState = {
      ...state,
      [field]: fieldError,
    };
  }

  return updatedState;
};

const PatientAddCoverage = ({
  coverage,
  onAdd,
  onDiscard,
  patientId,
  patientBasicDetails,
  patientCoverages,
}: PatientAddCoverageProps) => {
  const { data: states, isLoading: isGetStatesLoading } =
    useGetProviderStatesEnumQuery();
  const {
    data: patientRelationsToSubscriber,
    isLoading: isGetPatientRelationsToSubscriberLoading,
  } = useGetCoverageSubscriberRelationsToPatientQuery();

  const {
    alternativeId: coverageAlternativeId,
    coverageExternalId,
    coveragePriority: coveragePriorityValue,
    customPayorName,
    payor: payorName,
    isActive,
    groupId: groupIdValue,
    providerServicesPhoneNumber,
    patientRelationToSubscriber,
    subscriberDateOfBirth,
    subscriberFirstName: firstName,
    subscriberLastName: lastName,
    stateCode,
    subscriberId: subscriberIdValue,
    startDate: coverageStartDate,
    endDate: coverageEndDate,
  } = coverage ?? {};

  const getPatientRelationToSubscriber = (relation: string): string => {
    if (relation === null) return '';
    return relation;
  };
  const [selectedPayor, setSelectedPayor] = useState<string>(payorName);
  const [status, setStatus] = useState<string>(
    isActive === undefined || isActive ? 'Active' : 'Inactive',
  );
  const [groupId, setGroupId] = useState<string>(groupIdValue);
  const [subscriberId, setSubscriberId] = useState<string>(subscriberIdValue);
  const [alternativeId, setAlternativeId] = useState<string>(
    coverageAlternativeId,
  );
  const [payorState, setPayorState] = useState<string>('');
  const [startDate, setStartDate] = useState<Date>(
    coverageStartDate
      ? DateTime.fromFormat(
          coverageStartDate,
          DateFormat.shortYearMonthDay,
        ).toJSDate()
      : null,
  );
  const [endDate, setEndDate] = useState<Date>(
    coverageEndDate
      ? DateTime.fromFormat(
          coverageEndDate,
          DateFormat.shortYearMonthDay,
        ).toJSDate()
      : null,
  );
  const [phoneNumber, setPhoneNumber] = useState<string>(
    util.parsePhoneNumberWithDefaultCountry(providerServicesPhoneNumber) || '',
  );
  const [otherInsuranceCompany, setOtherInsuranceCompany] =
    useState<string>(customPayorName);
  const [relationToSubscriber, setRelationToSubscriber] = useState<string>(
    getPatientRelationToSubscriber(patientRelationToSubscriber) || '',
  );
  const [dateOfBirth, setDateOfBirth] = useState<Date>(
    subscriberDateOfBirth
      ? DateTime.fromFormat(
          subscriberDateOfBirth,
          DateFormat.shortYearMonthDay,
        ).toJSDate()
      : null,
  );
  const [subscriberFirstName, setSubscriberFirstName] =
    useState<string>(firstName);
  const [subscriberLastName, setSubscriberLastName] =
    useState<string>(lastName);
  const [isStartDateValid, setIsStartDateValid] = useState<boolean>(false);
  const [isEndDateValid, setIsEndDateValid] = useState<boolean>(false);
  const [isDateOfBirthValid, setIsDateOfBirthValid] = useState<boolean>(false);

  const [isUnsavedAddCoverageChanges, setIsUnsavedAddCoverageChanges] =
    useState<boolean>(false);
  const [coveragePriority, setCoveragePriority] = useState<number>(
    coveragePriorityValue,
  );
  const [
    openConfirmsPatientCoverageChangePriorityModal,
    setOpenConfirmsPatientCoverageChangePriorityModal,
  ] = useState<boolean>(false);
  const {
    error: submitAddCoverageError,
    loading: isSubmitAddCoverageLoading,
    sendRequest: submitAddCoverage,
    statusCode: submitAddCoverageStatusCode,
  } = useApi();

  const { resetPatientCoverages } = usePatientCoveragesDispatch();

  const [errors, dispatchErrors] = useReducer<AddCoverageErrorsReducer>(
    errorsReducer,
    {},
  );

  const IS_SELF = relationToSubscriber.toLowerCase() === 'self';
  const IS_NOT_SELF = relationToSubscriber?.toLowerCase() !== 'self';
  const OTHER_INSURANCE_IDENTIFIER = 'other';

  const statesMenuOptions = useMemo<MenuOption[]>(() => {
    setPayorState(states?.find((st) => st.externalId === stateCode)?.label);
    return (
      states
        ?.filter((state) => state.label !== 'Unknown')
        .map(toStateMenuOptionInCoverage) ?? []
    );
  }, [states?.length]);

  const patientRelationsToSubscriberOptions = useMemo<MenuOption[]>(
    () => (patientRelationsToSubscriber ?? []).map(toStateMenuOptionInCoverage),
    [patientRelationsToSubscriber?.length],
  );

  useEffect(() => {
    if (submitAddCoverageStatusCode === 200) {
      resetPatientCoverages(patientId);
      setIsUnsavedAddCoverageChanges(false);
      onAdd();
    } else if (submitAddCoverageError) {
      console.error('Error submitting admin episode: ', submitAddCoverageError);
    }
  }, [submitAddCoverageStatusCode, submitAddCoverageError]);

  const menuRightIcon = <AngleDown fill={primary[100]} />;

  const handlePayorSelect = (payor: string): void => {
    if (payor) {
      setSelectedPayor(payor);
    }
  };

  const checkRequiredFields = (): boolean => {
    const newErrors: AddCoverageErrors = {
      payor: !selectedPayor,
    };

    dispatchErrors({ field: 'SET', newState: newErrors });

    return Object.values(newErrors).some(
      (isError: boolean): boolean => isError,
    );
  };

  const handleStateFieldChange =
    <T,>(setState: Dispatch<SetStateAction<T>>) =>
    (value: T) => {
      setIsUnsavedAddCoverageChanges(true);
      setState(value);
    };

  const getSubscriberDateOfBirth = (date: Date): Date | string => {
    if (!relationToSubscriber) return '';
    return date
      ? DateTime.fromJSDate(date).toFormat(DateFormat.shortYearMonthDay)
      : '';
  };

  const getSubscriberName = (name: string): string => {
    if (!relationToSubscriber) return '';
    return name;
  };

  useEffect(() => {
    if (coverageExternalId) {
      if (relationToSubscriber.toLocaleLowerCase() === 'self') {
        setSubscriberFirstName('');
        setSubscriberLastName('');
        setDateOfBirth(null);
      }
    }
  }, [relationToSubscriber]);

  const onPatientCoveragePriorityChangeClose = () => {
    setCoveragePriority(null);
    setOpenConfirmsPatientCoverageChangePriorityModal(false);
  };

  const proceedToSave = () => {
    const hasErrors = checkRequiredFields();

    if (!hasErrors && proceedToSave) {
      const body = {
        alternativeId,
        coverageExternalId,
        coveragePriority,
        customPayorName:
          selectedPayor?.toLowerCase() === OTHER_INSURANCE_IDENTIFIER
            ? otherInsuranceCompany
            : '',
        endDate: endDate
          ? DateTime.fromJSDate(endDate).toFormat(DateFormat.shortYearMonthDay)
          : '',
        groupId,
        isActive: status === 'Active',
        patientExternalId: patientId,
        patientRelationToSubscriber: relationToSubscriber || '',
        payor: selectedPayor,
        payorProgram: '',
        providerServicesPhoneNumber: phoneNumber,
        startDate: startDate
          ? DateTime.fromJSDate(startDate).toFormat(
              DateFormat.shortYearMonthDay,
            )
          : '',
        stateCode: states.find((st) => st.label === payorState)?.externalId,
        subscriberDateOfBirth: IS_SELF
          ? patientBasicDetails.dateOfBirth
          : getSubscriberDateOfBirth(dateOfBirth),
        subscriberFirstName: IS_SELF
          ? patientBasicDetails.firstName
          : getSubscriberName(subscriberFirstName),
        subscriberId,
        subscriberLastName: IS_SELF
          ? patientBasicDetails.lastName
          : getSubscriberName(subscriberLastName),
      };
      const command = coverageExternalId ? ApiCommand.PUT : ApiCommand.POST;
      const url = coverageExternalId
        ? putCoverage(patientId, coverageExternalId)
        : postCoverage(patientId);
      submitAddCoverage({
        command,
        options: body,
        url,
      });
    }
  };
  const handleSubmitAddCoverage = () => {
    const isCoveragePrioryAlreadyExists =
      coveragePriority &&
      isCurrentCoveragePriorityAlreadyExists(patientCoverages, {
        coverageExternalId,
        coveragePriority,
      });
    if (status === 'Active' && isCoveragePrioryAlreadyExists) {
      setOpenConfirmsPatientCoverageChangePriorityModal(true);
    } else {
      proceedToSave();
    }
  };

  const { UnsavedChangesModal, guardedDismiss } = useUnsavedChangesGuard({
    isDirty: isUnsavedAddCoverageChanges,
    onDiscard,
  });

  const onPatientCoveragePriorityChangeConfirm = () => {
    setOpenConfirmsPatientCoverageChangePriorityModal(false);
    proceedToSave();
  };
  const CoverageMenu: <T extends string>(
    props: CoverageMenuProps<T>,
  ) => ReactElement<CoverageMenuProps<T>> = <T extends string>({
    buttonContent,
    id,
    isInvalid,
    isLoading,
    isRequired,
    label,
    setState,
    options,
    placeholder,
    withPlaceholderItem,
  }: CoverageMenuProps<T>) => (
    <Menu
      backgroundColor="neutral.background.secondary"
      buttonContent={buttonContent}
      buttonHeight="48px"
      buttonVariant="ghost"
      formMarginBottom="12px"
      icon={menuRightIcon}
      id={id}
      isFullWidth
      isInvalid={isInvalid}
      isLoading={isLoading}
      isRequired={isRequired}
      label={label}
      listMaxHeight="220px"
      onSelect={handleStateFieldChange<T>(setState)}
      options={options}
      overflow="scroll"
      placeholder={placeholder}
      textAlign="left"
      withPlaceholderItem={withPlaceholderItem}
    />
  );

  const isValidPhoneNumber = useMemo<boolean>(
    () => util.validatePhoneNumber(phoneNumber),
    [phoneNumber],
  );

  const isSaveChangesDisabled = useMemo(
    () =>
      !selectedPayor ||
      !isEndDateValid ||
      (phoneNumber && !isValidPhoneNumber) ||
      (relationToSubscriber?.toLowerCase() !== 'self' &&
        relationToSubscriber &&
        !isDateOfBirthValid) ||
      !isStartDateValid ||
      (selectedPayor?.toLowerCase() === OTHER_INSURANCE_IDENTIFIER &&
        (!otherInsuranceCompany ||
          (otherInsuranceCompany ?? '').length > MAX_NAME_LENGTH)),
    [
      otherInsuranceCompany,
      selectedPayor,
      isEndDateValid,
      phoneNumber,
      isValidPhoneNumber,
      relationToSubscriber,
      isDateOfBirthValid,
      isStartDateValid,
    ],
  );

  return (
    <ChakraBox paddingY="24px">
      <ConfirmModal
        body="This will affect the prioritization of active coverages."
        cancelLabel="Cancel"
        confirmLabel={`Set as ${getPatientCoveragePriorityTextByValue(
          coveragePriority,
        )}`}
        confirmVariant="accent.critical"
        isOpen={openConfirmsPatientCoverageChangePriorityModal}
        onClose={onPatientCoveragePriorityChangeClose}
        onConfirm={onPatientCoveragePriorityChangeConfirm}
        title={`Set as ${getPatientCoveragePriorityTextByValue(
          coveragePriority,
        )} coverage?`}
      />
      {UnsavedChangesModal}
      <ChakraSimpleGrid columns={3} paddingBottom="24px" spacing="36px">
        <ChakraGridItem>
          <PayorSelect
            label="Insurance company"
            onSelect={handlePayorSelect}
            selectedPayor={selectedPayor}
          />
          {selectedPayor?.toLowerCase() === OTHER_INSURANCE_IDENTIFIER && (
            <TextInput
              errorMessage="Other insurance company name must be between 1 - 60 characters"
              formMarginBottom="12px"
              id="add-patient-coverage__other__insurance-company"
              isInvalid={
                otherInsuranceCompany &&
                otherInsuranceCompany.length >= MAX_NAME_LENGTH
              }
              isRequired
              label="Other insurance company"
              onChange={(evt: ChangeEvent<HTMLInputElement>) => {
                setOtherInsuranceCompany(evt.target.value);
                setIsUnsavedAddCoverageChanges(true);
              }}
              value={otherInsuranceCompany}
            />
          )}
          <TextInput
            formMarginBottom="12px"
            id="add-patient-coverage__subscribe-id"
            label="Member ID"
            onChange={(evt: ChangeEvent<HTMLInputElement>) => {
              setSubscriberId(evt.target.value);
              setIsUnsavedAddCoverageChanges(true);
            }}
            value={subscriberId}
          />
          <TextInput
            formMarginBottom="12px"
            id="add-patient-coverage__group-id"
            label="Group ID"
            onChange={(evt: ChangeEvent<HTMLInputElement>) => {
              setGroupId(evt.target.value);
              setIsUnsavedAddCoverageChanges(true);
            }}
            value={groupId}
          />
          <TextInput
            formMarginBottom="12px"
            id="add-patient-coverage__alternative-id"
            label="Alternative ID"
            onChange={(evt: ChangeEvent<HTMLInputElement>) => {
              setAlternativeId(evt.target.value);
              setIsUnsavedAddCoverageChanges(true);
            }}
            value={alternativeId}
          />
        </ChakraGridItem>
        <ChakraGridItem>
          <CoverageMenu<string>
            buttonContent={payorState}
            id="add-patient-coverage__insurance-company-state"
            isInvalid={errors.stateCode}
            isLoading={isGetStatesLoading}
            label="Insurance company state"
            options={statesMenuOptions}
            placeholder="Select a insurance company state"
            setState={setPayorState}
          />
          <ChakraBox marginBottom="12px">
            <PhoneNumber
              id="add-patient-coverage__phone-number"
              isInvalid={phoneNumber && !isValidPhoneNumber}
              label="Provider services number"
              onChange={(value) => {
                setPhoneNumber(value);
                setIsUnsavedAddCoverageChanges(true);
              }}
              value={phoneNumber}
            />
          </ChakraBox>
          <CoverageMenu<string>
            buttonContent={
              patientRelationsToSubscriberOptions.some(
                (item) => item.value === relationToSubscriber,
              )
                ? relationToSubscriber
                : undefined
            }
            id="add-patient-coverage__relation-to-patient"
            isInvalid={false}
            isLoading={isGetPatientRelationsToSubscriberLoading}
            label="Patient relationship to subscriber"
            options={patientRelationsToSubscriberOptions}
            placeholder="Select one..."
            setState={setRelationToSubscriber}
            withPlaceholderItem
          />
          {IS_NOT_SELF && relationToSubscriber && (
            <TextInput
              formMarginBottom="12px"
              id="add-patient-coverage__subscriber__first-name"
              label="Subscriber first name"
              onChange={(evt: ChangeEvent<HTMLInputElement>) => {
                setSubscriberFirstName(evt.target.value);
                setIsUnsavedAddCoverageChanges(true);
              }}
              value={subscriberFirstName}
            />
          )}
          {IS_NOT_SELF && relationToSubscriber && (
            <TextInput
              formMarginBottom="12px"
              id="add-patient-coverage__subscriber__last-name"
              label="Subscriber last name"
              onChange={(evt: ChangeEvent<HTMLInputElement>) => {
                setSubscriberLastName(evt.target.value);
                setIsUnsavedAddCoverageChanges(true);
              }}
              value={subscriberLastName}
            />
          )}
          {IS_NOT_SELF && relationToSubscriber && (
            <DatePicker
              id="add-patient-coverage__date-of-birth"
              label="Subscriber DOB"
              maxDate={new Date()}
              onSelect={handleStateFieldChange<Date>(setDateOfBirth)}
              onValidateDate={(isValid: boolean): void => {
                setIsDateOfBirthValid(isValid);
              }}
              placeHolderText="MM/DD/YYYY"
              value={dateOfBirth}
            />
          )}
        </ChakraGridItem>
        <ChakraGridItem>
          <CoverageMenu<string>
            buttonContent={status}
            id="add-patient-coverage__status"
            isInvalid={errors.status}
            label="Status"
            options={[
              {
                node: 'Active',
                value: 'Active',
              },
              {
                node: 'Inactive',
                value: 'Inactive',
              },
            ]}
            placeholder="Select a status"
            setState={setStatus}
          />
          <Menu
            backgroundColor="neutral.background.secondary"
            buttonContent={getPatientCoveragePriorityTextByValue(
              coveragePriority,
            )}
            buttonHeight="48px"
            buttonVariant="ghost"
            formMarginBottom="12px"
            icon={menuRightIcon}
            id="add-patient-coverage__priority"
            isFullWidth
            label="Priority"
            listMaxHeight="220px"
            onSelect={(value) => setCoveragePriority(parseInt(value, 10))}
            options={getPatientCoveragePriorityOptions(patientCoverages)}
            overflow="scroll"
            placeholder="Select a coverage priority"
            textAlign="left"
          />
          <ChakraBox marginBottom="12px">
            <DatePicker
              id="add-patient-coverage__start-date"
              label="Start date"
              marginBottom="10px"
              onSelect={handleStateFieldChange<Date>(setStartDate)}
              value={startDate}
              {...(endDate ? { maxDate: new Date(endDate) } : {})}
              onValidateDate={(isValid: boolean): void => {
                setIsStartDateValid(isValid);
              }}
              placeHolderText="MM/DD/YYYY"
            />
          </ChakraBox>
          <ChakraBox marginBottom="12px">
            <DatePicker
              id="add-patient-coverage__end-date"
              label="End date"
              marginBottom="10px"
              minDate={new Date(startDate)}
              onSelect={handleStateFieldChange<Date>(setEndDate)}
              onValidateDate={(isValid: boolean): void => {
                setIsEndDateValid(isValid);
              }}
              placeHolderText="MM/DD/YYYY"
              value={endDate}
            />
          </ChakraBox>
        </ChakraGridItem>
      </ChakraSimpleGrid>
      <ChakraFlex>
        <Button
          colorScheme="gray"
          id="add-patient-coverage__discard"
          onClick={guardedDismiss}
          variant="ghost"
        >
          Discard
        </Button>
        <ChakraSpacer />
        <Button
          id="add-patient-coverage__save"
          isDisabled={isSaveChangesDisabled}
          isLoading={isSubmitAddCoverageLoading}
          onClick={handleSubmitAddCoverage}
        >
          Save Changes
        </Button>
      </ChakraFlex>
    </ChakraBox>
  );
};

export default PatientAddCoverage;
